import LoadingOverlay from '@/components/loading/LoadingOverlay';
import { GroupedOption } from '@/components/ui/react-select/ReactGroupSelect';
import { ConfigDataType } from '@/types';
import { mapCategoriesToGroupedOptions } from '@/utils/mapData';
import { useQueries } from '@tanstack/react-query';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import ErrorSection from '@/components/ui/ErrorSection';
import useApiRequest from '@/hooks/common/useApiRequest';
import { API_ENDPOINTS, QUERY_KEYS } from '@/config/const';

interface ConfigContextValue {
  data: ConfigDataType;
  categoriesGrouped: GroupedOption[];
}

const defaultData: ConfigDataType = {
  upworkCrawlerCategories: [],
  upworkCrawlerExperienceLevels: [],
  upworkCrawlerLocations: [],
  googleSheetsSpreadsheet: null,
};

const ConfigContext = createContext<ConfigContextValue>({
  data: defaultData,
  categoriesGrouped: [],
});

export const ConfigProvider = ({ children }: { children: ReactNode }) => {
  const { fetchData } = useApiRequest();

  const endpoints = [
    { key: [QUERY_KEYS.upworkCrawlerCategories], url: API_ENDPOINTS.categories },
    {
      key: [QUERY_KEYS.upworkCrawlerExperienceLevels],
      url: API_ENDPOINTS.experienceLevels,
    },
    { key: [QUERY_KEYS.upworkCrawlerLocations], url: API_ENDPOINTS.locations },
    { key: [QUERY_KEYS.googleSheetsSpreadsheet], url: API_ENDPOINTS.spreadsheet },
  ];

  const { data, isLoading, hasError } = useQueries({
    queries: endpoints.map(({ key, url }) => ({
      queryKey: key,
      queryFn: () => fetchData(url),
    })),
    combine: (results) => {
      const data = results.reduce(
        (acc: Record<string, any>, result, index) => {
          const key = endpoints[index].key[0];
          acc[key] = result.data;
          return acc;
        },
        {} as Record<string, any>
      );

      const isLoading = results.some((result) => result.isPending);
      const errors = results.filter((result) => result.isError).map((result) => result.error);

      return {
        data,
        isLoading,
        hasError: errors.length > 0,
      };
    },
  });

  const state = useMemo(() => {
    if (!isLoading && !hasError && data) {
      const categoriesGrouped = mapCategoriesToGroupedOptions(data.upworkCrawlerCategories);
      return {
        categoriesGrouped,
      };
    }

    return {
      categoriesGrouped: [],
    };
  }, [isLoading, hasError, data]);

  if (isLoading) return <LoadingOverlay />;

  if (hasError)
    return (
      <div className='flex justify-center items-center px-4 py-8'>
        <ErrorSection />
      </div>
    );

  return (
    <ConfigContext.Provider value={{ data: data as ConfigDataType, ...state }}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfigContext = (): ConfigContextValue => {
  const context = useContext(ConfigContext) as ConfigContextValue;
  if (!context) {
    throw new Error('useConfigContext must be used within an ConfigProvider');
  }
  return context;
};
